import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { SignedNumber } from '@dndbeyond/character-common-components/es';
import { AbilityScoreManager, HtmlContent, AbilityIcon } from '@dndbeyond/character-components/es';
import {
    Ability,
    AbilityLookup,
    AbilityUtils,
    rulesEngineSelectors,
    characterActions,
    Constants,
    FormatUtils,
    RuleData,
    RuleDataUtils,
    StatDataContract,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';

import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeaderPreview, SidebarHeader } from '../../../components/Sidebar';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneIdentifiersAbility } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    abilityLookup: AbilityLookup;
    ruleData: RuleData;
    identifiers: PaneIdentifiersAbility | null;
    isReadonly: boolean;
    theme: CharacterTheme;
}
interface State {
    ability: Ability | null;
    abilityData: StatDataContract | null;
}
class AbilityPane extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = this.generateStateData(props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { abilityLookup, identifiers } = this.props;

        if (abilityLookup !== prevProps.abilityLookup || identifiers !== prevProps.identifiers) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { ruleData, identifiers, abilityLookup } = props;

        const abilityDataLookup = RuleDataUtils.getStatsLookup(ruleData);
        let foundAbility: Ability | null | undefined = null;
        let foundAbilityData: StatDataContract | null | undefined = null;

        if (identifiers) {
            foundAbility = abilityLookup[identifiers.id];
            foundAbilityData = abilityDataLookup[identifiers.id];
        }

        return {
            ability: foundAbility ? foundAbility : null,
            abilityData: foundAbilityData ? foundAbilityData : null,
        };
    };

    handleOverrideScoreChange = (statId: number, value: number | null): void => {
        const { dispatch } = this.props;

        dispatch(characterActions.abilityScoreSet(statId, Constants.AbilityScoreStatTypeEnum.OVERRIDE, value));
    };

    handleOtherBonusChange = (statId: number, value: number | null): void => {
        const { dispatch } = this.props;

        dispatch(characterActions.abilityScoreSet(statId, Constants.AbilityScoreStatTypeEnum.BONUS, value));
    };

    render() {
        const { ability, abilityData } = this.state;
        const { ruleData, isReadonly, theme } = this.props;

        if (!ability) {
            return <PaneInitFailureContent />;
        }

        const statId = AbilityUtils.getId(ability);
        const statKey = AbilityUtils.getStatKey(ability);
        let sidebarPreviewClassNames: Array<string> = ['ct-ability-pane__icon'];
        sidebarPreviewClassNames.push(`ct-ability-pane__icon--${statKey}`);

        let sidebarPreviewNode: React.ReactNode = (
            <SidebarHeaderPreview>
                <AbilityIcon
                    className={sidebarPreviewClassNames.join(' ')}
                    statId={statId}
                    themeMode={theme.isDarkMode ? 'light' : 'dark'}
                />
            </SidebarHeaderPreview>
        );

        const modifier = AbilityUtils.getModifier(ability);
        let abilityName: string = abilityData && abilityData.name ? abilityData.name : '';

        return (
            <div className="ct-ability-pane" key={statId}>
                <SidebarHeader preview={sidebarPreviewNode}>
                    {abilityName} {AbilityUtils.getTotalScore(ability)}
                    {modifier !== null && (
                        <span className="ct-ability-pane__modifier">
                            (<SignedNumber number={modifier} />)
                        </span>
                    )}
                </SidebarHeader>
                <AbilityScoreManager
                    ability={ability}
                    onOtherBonusChange={this.handleOtherBonusChange}
                    onOverrideScoreChange={this.handleOverrideScoreChange}
                    showHeader={false}
                    ruleData={ruleData}
                    isReadonly={isReadonly}
                />
                {abilityData && abilityData.compendiumText && (
                    <HtmlContent className="ct-ability-pane__description" html={abilityData.compendiumText} />
                )}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        ruleData: rulesEngineSelectors.getRuleData(state),
        abilityLookup: rulesEngineSelectors.getAbilityLookup(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
    };
}

export default connect(mapStateToProps)(AbilityPane);
